import { useState } from 'react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { newsletterAPI } from '../lib/api';
import { toast } from 'sonner';
import { Mail, Check, ArrowRight } from 'lucide-react';

export const NewsletterPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      await newsletterAPI.subscribe(email);
      setSubscribed(true);
      toast.success('Successfully subscribed!');
    } catch (err) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    'Weekly roundup of Portland tech news',
    'Upcoming events and meetups',
    'New job postings from local companies',
    'Startup announcements and funding news',
    'Community highlights and success stories',
  ];

  return (
    <div data-testid="newsletter-page">
      {/* Header */}
      <section className="border-b border-border">
        <div className="container-wide py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-label text-muted-foreground mb-4 block">Stay Connected</span>
              <h1 className="text-display text-4xl lg:text-6xl mb-6">
                Portland Tech<br />Newsletter
              </h1>
              <p className="text-lg text-muted-foreground mb-8 max-w-lg">
                Get weekly updates on Portland's technology ecosystem delivered 
                straight to your inbox. News, events, jobs, and community updates.
              </p>
              
              {subscribed ? (
                <Card className="border-green-500/20 bg-green-500/5">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                      <Check className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold">You're subscribed!</h3>
                      <p className="text-sm text-muted-foreground">
                        Check your inbox for a welcome email.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <form onSubmit={handleSubscribe} className="flex gap-3 max-w-md">
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 h-12"
                    required
                    data-testid="newsletter-email"
                  />
                  <Button type="submit" size="lg" className="h-12 px-6" disabled={loading} data-testid="newsletter-submit">
                    {loading ? 'Subscribing...' : 'Subscribe'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>
              )}
            </div>

            <div className="bg-muted/50 p-8 rounded-sm">
              <h3 className="font-semibold text-lg mb-6">What you'll receive:</h3>
              <ul className="space-y-4">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                      <Check className="w-3 h-3 text-primary" />
                    </div>
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Archive Section */}
      <section className="bg-muted/30">
        <div className="container-wide py-16">
          <div className="text-center max-w-2xl mx-auto">
            <Mail className="w-12 h-12 mx-auto mb-6 text-muted-foreground" />
            <span className="text-label text-muted-foreground mb-4 block">Coming Soon</span>
            <h2 className="text-heading text-2xl mb-4">Newsletter Archive</h2>
            <p className="text-muted-foreground">
              Browse past issues of the Portland Tech Newsletter. We're working on 
              making our archive accessible to everyone.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
